import { AnyAction } from 'redux'
import { createSelector } from 'reselect'
import { CONTENT_UPDATE, SIMILAR_TEMPLATES_INJECT, SIMILAR_TEMPLATES_RESET } from 'client/store/constants'
import { getQuantitySelector, getLocaleSelector, stringRenderPropertySelector, booleanRenderPropertySelector, getViewId } from 'client/store/config'
import { RenderProperty } from 'shared/renderProperties'
import { DESIGN_CREATION_TYPE, PREVIEW_TYPE } from 'shared/constants'
import { tileEntityByIdSelector } from 'client/store/tileEntity'
import { getPreviewData } from 'client/utils/previewData'
import { buildFullyQualifiedVistaprintUrl } from 'client/utils/vistaprintUrlBuilder'
import { getLogger } from 'shared/logger'
import { designPersonalizationContextIdSelector } from 'client/store/designPersonalization/selectors'
import { similarDesignByIdSelector, similarTemplatesByIdSelector } from '~/client/store/similarTemplates/reducer'
import { getDefaultColorSwatch } from '~/client/store/design/utils'

const initialState: State.DesignState = {
  byId: {},
  allIds: [],
}

const updateState = (action: AnyAction, state: State.DesignState = initialState): State.DesignState => ({
  ...state,
  byId: action.payload.designs.byId,
  allIds: action.payload.designs.allIds,
})

export function reducer(state: State.DesignState = initialState, action: AnyAction): State.DesignState {
  switch (action.type) {
    case CONTENT_UPDATE:
      return updateState(action, state)
    case SIMILAR_TEMPLATES_INJECT:
      return {
        ...state,
        byIdSimilarTemplates: {
          ...state.byIdSimilarTemplates,
          ...action.payload.designs.byId,
        },
      }
    case SIMILAR_TEMPLATES_RESET:
      delete state.byIdSimilarTemplates
      return state
    default:
      return state
  }
}

export const designByIdSelector = createSelector(
  (state: State.GlobalState) => state.designs,
  (designs) =>
    (id: string): State.Design =>
      ({ ...designs.byId, ...designs.byIdSimilarTemplates })[id],
)

export const renderablePreviewInfoSelector = createSelector(
  getViewId,
  getLocaleSelector,
  booleanRenderPropertySelector,
  stringRenderPropertySelector,
  designByIdSelector,
  tileEntityByIdSelector,
  designPersonalizationContextIdSelector,
  (viewId, locale, booleanRenderProperty, stringRenderProperty, designById, tileEntityById, designPersonalizationContextId) =>
    (
      currentDesignId: string,
      entityId: string,
      previewType: PREVIEW_TYPE = PREVIEW_TYPE.TILE,
      ignorePersonalization = false, // For instance, if selecting from favorites
      previewInfoProp?: Gallery.ContentQuery.PreviewInfo,
    ): Gallery.Designs.RenderablePreviewInfo => {
      const previewInfo = previewInfoProp ?? designById(currentDesignId).previewInfo
      const { previewUrls } = designById(currentDesignId)
      const showPersonalizationUI = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
      const smartCropEnabled = booleanRenderProperty(RenderProperty.PersonalizationSmartCropEnabled)
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)

      const { productKey, productVersion, fullProductOptions, designCreationType } = tileEntityById(entityId)

      return getPreviewData(
        previewInfo,
        locale,
        previewType,
        previewUrls,
        showPersonalizationUI,
        designCreationType === DESIGN_CREATION_TYPE.DYNAMIC,
        tilePreviewSize,
        imageCacheVersion,
        productKey,
        productVersion,
        fullProductOptions,
        !ignorePersonalization ? designPersonalizationContextId : undefined,
        viewId,
        smartCropEnabled,
      )
    },
)

export const colorSwatchSelector = createSelector(
  renderablePreviewInfoSelector,
  designByIdSelector,
  tileEntityByIdSelector,
  (renderablePreviewInfo, designById, tileEntityById) =>
    (designId: string, entityId: string, previewType: PREVIEW_TYPE, ignorePersonalization = false): Gallery.Designs.CurrentColorSwatch => {
      const renderablePreviews = renderablePreviewInfo(designId, entityId, previewType, ignorePersonalization)
      const { color, studioUrl } = designById(designId)
      const { colorSwatches } = tileEntityById(entityId)
      const currentColorSwatchId = colorSwatches.find((cs) => cs === designId) as string
      const currentColorSwatch = designById(currentColorSwatchId)

      return {
        ...currentColorSwatch,
        color,
        designId,
        previewType,
        renderablePreviews,
        studioUrl,
        colorSwatches: colorSwatches.map(designById).filter(Boolean),
      }
    },
)

export const renderableSimilarPreviewInfoSelector = createSelector(
  getLocaleSelector,
  booleanRenderPropertySelector,
  stringRenderPropertySelector,
  similarDesignByIdSelector,
  similarTemplatesByIdSelector,
  designPersonalizationContextIdSelector,
  (locale, booleanRenderProperty, stringRenderProperty, designById, tileEntityById, designPersonalizationContextId) =>
    (
      currentDesignId: string,
      entityId: string,
      previewType: PREVIEW_TYPE = PREVIEW_TYPE.TILE,
      ignorePersonalization = false, // For instance, if selecting from favorites
      previewInfoProp?: Gallery.ContentQuery.PreviewInfo,
    ): Gallery.Designs.RenderablePreviewInfo => {
      const previewInfo = previewInfoProp ?? designById(currentDesignId).previewInfo
      const { previewUrls } = designById(currentDesignId)
      const showPersonalizationUI = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
      const smartCropEnabled = booleanRenderProperty(RenderProperty.PersonalizationSmartCropEnabled)
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)
      const { productKey, productVersion, fullProductOptions, designCreationType } = tileEntityById(entityId)

      return getPreviewData(
        previewInfo,
        locale,
        previewType,
        previewUrls,
        showPersonalizationUI,
        designCreationType === DESIGN_CREATION_TYPE.DYNAMIC,
        tilePreviewSize,
        imageCacheVersion,
        productKey,
        productVersion,
        fullProductOptions,
        !ignorePersonalization ? designPersonalizationContextId : undefined,
        undefined,
        smartCropEnabled,
      )
    },
)

export const similarColorSwatchSelector = createSelector(
  renderableSimilarPreviewInfoSelector,
  similarDesignByIdSelector,
  similarTemplatesByIdSelector,
  (state: State.GlobalState) => state.similarTemplates.designs,
  (renderablePreviewInfo, designById, tileEntityById, designs) =>
    (designId: string, entityId: string, previewType: PREVIEW_TYPE, ignorePersonalization = false): Gallery.Designs.CurrentColorSwatch => {
      const renderablePreviews = renderablePreviewInfo(designId, entityId, previewType, ignorePersonalization)

      const { color, studioUrl } = designById(designId)
      const { colorSwatches } = tileEntityById(entityId)
      const currentColorSwatchId = colorSwatches.find((cs) => cs === designId) as string
      const currentColorSwatch = designs.byId[currentColorSwatchId]

      return {
        ...currentColorSwatch,
        color,
        designId,
        previewType,
        renderablePreviews,
        studioUrl,
        colorSwatches: colorSwatches.map((cs) => designs.byId[cs]).filter(Boolean),
      }
    },
)

export const colorSwatchSelectorWithDesignVariation = createSelector(
  designByIdSelector,
  colorSwatchSelector,
  getLocaleSelector,
  stringRenderPropertySelector,
  getViewId,
  booleanRenderPropertySelector,
  designPersonalizationContextIdSelector,
  tileEntityByIdSelector,
  getQuantitySelector,
  (designById, colorSwatch, locale, stringRenderProperty, viewId, booleanRenderProperty, designPersonalizationContextId, tileEntityById, quantity) =>
    (
      designId: string,
      entityId: string,
      designVariation: DesignVariations.DesignVariationResponse | undefined,
      previewType: PREVIEW_TYPE,
      impressionId?: string,
      selectedProductOptions?: Gallery.ContentQuery.ProductOptions,
    ): Gallery.Designs.CurrentColorSwatch => {
      const currentColorSwatch = designVariation?.colorSwatches.length
        ? designVariation?.colorSwatches.find((c) => c.designId === designId)
        : ((designVariation || {}) as Gallery.ContentQuery.ColorSwatch)

      if (!currentColorSwatch || !designVariation) {
        try {
          // Hack to handle the case where the design variation is not available and designId is not in Redux state.
          // It happens when user goes back to prev template after selecting template from "More like this" section.
          if (!designById(designId)) {
            return getDefaultColorSwatch(designId, previewType)
          }

          return colorSwatch(designId, entityId, previewType)
        } catch (e) {
          getLogger().error(
            {
              designId,
              entityId,
              designVariation,
              code: 'SD001',
              message: `Current color swatch is null and old designID is being used: ${(e as Error).message}`,
            },
            e as Error,
          )
          throw e
        }
      }
      const { colorSwatches, studioUrl } = designVariation
      const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize)
      const imageCacheVersion = stringRenderProperty(RenderProperty.ImageCacheVersion)
      const showPersonalizationUI = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
      const smartCropEnabled = booleanRenderProperty(RenderProperty.PersonalizationSmartCropEnabled)

      const { productKey, productVersion, fullProductOptions, designCreationType } = tileEntityById(entityId)
      const { color, previewUrls, isLightColor } = currentColorSwatch
      const renderablePreviews = getPreviewData(
        currentColorSwatch ? currentColorSwatch.previewInfo : designVariation.previewInfo,
        locale,
        PREVIEW_TYPE.QUICKVIEW,
        previewUrls,
        showPersonalizationUI,
        designCreationType === DESIGN_CREATION_TYPE.DYNAMIC,
        tilePreviewSize,
        imageCacheVersion,
        productKey,
        productVersion,
        {
          // Contains the initial, complete selections. These need to be complete for DSS calls
          ...fullProductOptions,
          // Overwrite with the most correct selections (e.g., selections in QV)
          ...selectedProductOptions,
        },
        designPersonalizationContextId,
        viewId,
        smartCropEnabled,
      )

      return {
        color,
        colorSwatches,
        designId,
        previewType,
        renderablePreviews,
        previewUrls,
        isLightColor,
        externalId: currentColorSwatch.externalId,
        designColors: currentColorSwatch.designColors,
        colorComposition: currentColorSwatch.colorComposition,
        previewInfo: currentColorSwatch ? currentColorSwatch.previewInfo : designVariation.previewInfo,
        studioUrl: buildFullyQualifiedVistaprintUrl({
          path: currentColorSwatch ? currentColorSwatch.studioUrl : studioUrl,
          locale,
          quantity,
          impressionId,
        }),
      }
    },
)
